import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { decryptToken } from "../utils/getToken";
import CorsOriginModal from "./CorsOriginModal";

const PERMANENT_ORIGINS = new Set([
  "http://localhost:3000",
  "http://127.0.0.1:3000",
  "https://beentara.com",
  "https://www.beentara.com",
]);

const CorsOrigins = () => {
  const urlmain = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${urlmain}/cors-origins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error(
          response.status === 404
            ? "CORS API not found on server. Deploy the latest Backend files and restart the API."
            : `Server returned ${response.status} (expected JSON, got HTML).`
        );
      }
      const res = await response.json();
      if (response.ok && !res.isError) {
        setRows(res.data || []);
      } else {
        toast.error(res.message || "Failed to load access URLs");
      }
    } catch (error) {
      toast.error(error.message || "Failed to load access URLs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const filteredRows = rows.filter((row) =>
    Object.values(row).some((value) =>
      String(value ?? "").toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const toggleStatus = async (id) => {
    try {
      const response = await fetch(`${urlmain}/cors-origins/toggle-status/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await response.json();
      if (response.ok && !res.isError) {
        setRows((prev) =>
          prev.map((row) =>
            row.id === id ? { ...row, status: res.status } : row
          )
        );
        toast.success(res.message);
      } else {
        toast.error(res.message || "Toggle failed");
      }
    } catch (error) {
      toast.error(error.message || "Toggle failed");
    }
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Remove this URL?",
      text: row.origin,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Remove",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${urlmain}/cors-origins/${row.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await response.json();
      if (response.ok && !res.isError) {
        setRows((prev) => prev.filter((item) => item.id !== row.id));
        toast.success(res.message);
      } else {
        toast.error(res.message || "Delete failed");
      }
    } catch (error) {
      toast.error(error.message || "Delete failed");
    }
  };

  return (
    <>
      <div className="oms-header">
        <h1 className="dashboard-title">CORS Access URLs</h1>
        <button
          className="oms-add-btn"
          onClick={() => {
            setEditRow(null);
            setIsModalOpen(true);
          }}
        >
          Add URL
        </button>
      </div>

      <p style={{ marginBottom: "12px", color: "#666" }}>
        Manage storefront and admin URLs allowed to call the API. New admin website
        links are added here automatically when you create an admin account.
      </p>

      <div className="filter-search">
        <div className="filter-search-input">
          <input
            type="search"
            placeholder="Search origins..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <i className="fa fa-search"></i>
        </div>
      </div>

      <div className="customer-table">
        <table>
          <thead>
            <tr>
              <th>Origin URL</th>
              <th>Label</th>
              <th>Admin ID</th>
              <th>Source</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  Loading...
                </td>
              </tr>
            ) : filteredRows.length > 0 ? (
              filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.origin}
                    {PERMANENT_ORIGINS.has(row.origin) && (
                      <span style={{ marginLeft: 8, color: "#059669", fontSize: 12 }}>
                        (always allowed)
                      </span>
                    )}
                  </td>
                  <td>{row.label || "—"}</td>
                  <td>{row.admin_main_id || "—"}</td>
                  <td>{row.source}</td>
                  <td>
                    {PERMANENT_ORIGINS.has(row.origin) ? (
                      <span style={{ color: "#059669" }}>Active</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleStatus(row.id)}
                        className="toggle-btn"
                        title={row.status === "Active" ? "Deactivate" : "Activate"}
                      >
                        <i
                          className={`fa ${
                            row.status === "Active" ? "fa-toggle-on" : "fa-toggle-off"
                          }`}
                        ></i>
                      </button>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => {
                          setEditRow(row);
                          setIsModalOpen(true);
                        }}
                      >
                        <i className="fa-solid fa-pencil"></i>
                      </button>
                      {!PERMANENT_ORIGINS.has(row.origin) && (
                        <button
                          type="button"
                          title="Delete"
                          className="delete-btn"
                          onClick={() => handleDelete(row)}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  No access URLs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CorsOriginModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        editRow={editRow}
        fetchRows={fetchRows}
      />
    </>
  );
};

export default CorsOrigins;
